/**
 * Typed fetch wrapper.
 *
 *  - Cookie-based session auth (server sets HttpOnly cookie; we always send credentials).
 *  - Idempotency-Key auto-attached to mutating requests with uuidv7-ish prefix.
 *  - Throws ApiError on non-2xx, with the `{ error: { code, message } }` body parsed.
 */
import type { ApiError } from './types';

export class ApiCallError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly detail?: unknown;
  constructor(status: number, body: ApiError, raw?: string) {
    super(body.message ?? `HTTP ${status}`);
    this.name = 'ApiCallError';
    this.status = status;
    this.code = body.code ?? 'unknown_error';
    if (body.detail !== undefined) this.detail = body.detail;
    if (raw !== undefined) this.raw = raw;
  }
  public raw?: string;
}

const MUTATING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/**
 * Codes the auth/session middleware emits when there is no usable session:
 *   - missing_session: no cookie / bearer token at all ("Token missing").
 *   - invalid_session: a token was present but failed verification — which
 *     includes the JWT having expired (jose throws on the `exp` claim).
 * Any 401 carrying one of these means the user must re-authenticate. Other
 * 401 codes (invalid_credentials, invalid_password, invalid_mfa_code,
 * reauth_failed) are deliberate login / step-up checks, NOT a dead session.
 */
const SESSION_DEAD_CODES = new Set(['missing_session', 'invalid_session']);

/**
 * Single-session "your session was forcibly ended" reasons (FR-1/2/3). These are
 * distinct from a plain expired session (SESSION_DEAD_CODES above): they get a
 * blocking popup with a specific message rather than the generic expiry toast.
 * The string values are the contract shared with the backend SSE `terminate`
 * reasons (apps/auth-svc/src/sessionBus.ts) and the modal i18n keys.
 */
export type SessionKillReason =
  | 'superseded'
  | 'ip_changed'
  | 'concurrent_tab'
  | 'logged_out'
  | 'suspended';

/** Maps the backend 401 error codes to a kill reason. */
const REASON_BY_CODE: Record<string, SessionKillReason> = {
  session_superseded: 'superseded',
  session_ip_changed: 'ip_changed',
  session_revoked: 'logged_out',
  account_suspended: 'suspended',
};

/**
 * App-level callback fired once a request proves the session is dead, so the
 * app can drop cached auth state and bounce the user to /login. Held as a
 * module singleton because this plain (non-React) fetch wrapper has no other
 * way to reach React state. SessionContext registers it on mount.
 */
let sessionExpiredHandler: (() => void) | null = null;
export function setOnSessionExpired(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

/**
 * Like sessionExpiredHandler but for a FORCED logout (single-session): carries a
 * reason so SessionContext can show the matching blocking popup. Registered by
 * SessionContext; also driven by the SSE stream and the multi-tab guard.
 */
let sessionKilledHandler: ((reason: SessionKillReason) => void) | null = null;
export function setOnSessionKilled(handler: ((reason: SessionKillReason) => void) | null): void {
  sessionKilledHandler = handler;
}

function newIdempotencyKey(): string {
  // Cheap UUIDv7-ish: 13-char hex timestamp + 12 random hex chars.
  const ts = Date.now().toString(16).padStart(13, '0');
  const rand = Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${ts}-${rand}`;
}

export type ApiRequestInit = Omit<RequestInit, 'body'> & {
  json?: unknown;
  idempotencyKey?: string | null; // pass null to suppress auto-key
};

export async function apiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('content-type') && init.json !== undefined) {
    headers.set('content-type', 'application/json');
  }
  headers.set('accept', 'application/json');

  const method = (init.method ?? 'GET').toUpperCase();
  if (MUTATING.has(method) && init.idempotencyKey !== null) {
    headers.set('idempotency-key', init.idempotencyKey ?? newIdempotencyKey());
  }

  const resp = await fetch(path, {
    ...init,
    method,
    credentials: 'include',
    headers,
    body: init.json !== undefined ? JSON.stringify(init.json) : undefined,
  });

  const text = await resp.text();
  let parsed: unknown = text;
  if (text && resp.headers.get('content-type')?.includes('application/json')) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // leave as text
    }
  }

  if (!resp.ok) {
    const errBody =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? (parsed as { error: ApiError }).error
        : { code: 'http_error', message: typeof parsed === 'string' ? parsed : `HTTP ${resp.status}` };
    // Session died. A forced single-session logout (superseded / IP changed /
    // revoked / suspended) gets the reason-specific popup; a plain expiry gets
    // the generic toast. Either way we drop cached auth and bounce to /login
    // instead of leaving the user on a raw 401.
    if (resp.status === 401) {
      const code = errBody.code ?? '';
      const killReason = REASON_BY_CODE[code];
      if (killReason && sessionKilledHandler) {
        sessionKilledHandler(killReason);
      } else if (SESSION_DEAD_CODES.has(code)) {
        sessionExpiredHandler?.();
      }
    }
    throw new ApiCallError(resp.status, errBody, text);
  }
  return parsed as T;
}

/**
 * Convenience helpers.
 */
export const apiGet = <T>(path: string, init?: Omit<ApiRequestInit, 'method' | 'json'>) =>
  apiRequest<T>(path, { ...init, method: 'GET' });
export const apiPost = <T>(path: string, json?: unknown, init?: Omit<ApiRequestInit, 'method'>) =>
  apiRequest<T>(path, { ...init, method: 'POST', json });
export const apiPatch = <T>(path: string, json?: unknown, init?: Omit<ApiRequestInit, 'method'>) =>
  apiRequest<T>(path, { ...init, method: 'PATCH', json });
export const apiDelete = <T>(path: string, init?: Omit<ApiRequestInit, 'method'>) =>
  apiRequest<T>(path, { ...init, method: 'DELETE' });

/**
 * Render any React Query / fetch error as a one-line message.
 * ApiCallError shows server's `code: message`; everything else (network
 * failures, CORS rejections, TypeError from a non-JSON response, etc.)
 * surfaces the underlying Error message so the user can diagnose, instead
 * of the previous opaque 'Error' fallback.
 */
export function describeError(err: unknown, fallback = 'Error'): string {
  if (err instanceof ApiCallError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
